import { create } from 'zustand';
import { HomeService } from '../services/home.service';
import { EventSectionData, GettingStartedSectionData, HomePageData, LeaderboardSectionData, QuoteSectionData, RecordingsSectionData } from '../types/home.types';

/**
 * Interface for the home store state
 */
interface HomeState {
  // Data
  homePageData: HomePageData | null;

  // UI states
  isLoading: boolean;
  error: string | null;
  isFetching: boolean;

  // Actions
  fetchHomePageData: () => Promise<void>;
  updateGettingStartedSection: (data: Partial<GettingStartedSectionData>) => Promise<void>;
  updateEventSection: (data: Partial<EventSectionData>) => Promise<void>;
  updateQuoteSection: (data: Partial<QuoteSectionData>) => Promise<void>;
  updateRecordingsSection: (data: Partial<RecordingsSectionData>) => Promise<void>;
  updateLeaderboardSection: (data: Partial<LeaderboardSectionData>) => Promise<void>;
  resetError: () => void;
}

/**
 * Home store for managing home page data and state
 */
export const useHomeStore = create<HomeState>((set, get) => ({
  // Initial state
  homePageData: null,
  isLoading: false,
  error: null,
  isFetching: false,

  /**
   * Fetch home page data from the API
   * Prevents concurrent calls and avoids unnecessary fetches
   */
  fetchHomePageData: async () => {
    const { isFetching, homePageData } = get();

    // Skip if already fetching
    if (isFetching) {
      return;
    }

    try {
      // Set loading and fetching state
      set({ isLoading: !homePageData, isFetching: true, error: null });

      // Fetch data from API
      const data = await HomeService.getHomePageData();

      // Update state with fetched data
      set({
        homePageData: data,
        isLoading: false,
        isFetching: false
      });
    } catch (error) {
      console.error('Error fetching home page data:', error);

      // Update error state
      set({
        error: 'Failed to load home page data. Please try again later.',
        isLoading: false,
        isFetching: false
      });
    }
  },

  /**
   * Reset error state
   */
  resetError: () => {
    set({ error: null });
  },

  /**
   * Update Event section data
   * @param data Updated Event section data
   */
  updateEventSection: async (data: Partial<EventSectionData>) => {
    try {
      // Set loading state
      set({ isLoading: true, error: null });

      // Call API to update Event section data
      const updatedEventSection = await HomeService.updateEventSectionData(data);

      // Update store with new data
      set((state) => ({
        homePageData: state.homePageData
          ? {
            ...state.homePageData,
            eventSection: updatedEventSection,
          }
          : null,
        isLoading: false,
      }));

      // No return value needed for Promise<void>
    } catch (error) {
      // Handle error
      console.error('Failed to update Event section:', error);

      // Set error state
      set({
        error: error instanceof Error ? error.message : 'Failed to update Event section',
        isLoading: false,
      });

      throw error;
    }
  },

  /**
   * Update Getting Started section data
   * @param data Updated Getting Started section data
   */
  updateGettingStartedSection: async (data: Partial<GettingStartedSectionData>) => {
    try {
      // Set loading state
      set({ isLoading: true, error: null });

      // Update data via API
      const updatedData = await HomeService.updateGettingStartedSectionData(data);

      // Update store with new data
      const currentHomePageData = get().homePageData;

      if (currentHomePageData) {
        set({
          homePageData: {
            ...currentHomePageData,
            gettingStartedSection: updatedData
          },
          isLoading: false
        });
      } else {
        // If homePageData is null, create a new object with just the gettingStartedSection
        set({
          homePageData: {
            metrics: [],
            gettingStartedSection: updatedData
          },
          isLoading: false
        });
      }
    } catch (error) {
      console.error('Error updating Getting Started section:', error);

      // Update error state
      set({
        error: 'Failed to update Getting Started section. Please try again later.',
        isLoading: false
      });
    }
  },

  /**
   * Update Quote section data
   * @param data Updated Quote section data
   */
  updateQuoteSection: async (data: Partial<QuoteSectionData>) => {
    try {
      // Set loading state
      set({ isLoading: true, error: null });

      // Call API to update Quote section data
      const updatedQuoteSection = await HomeService.updateQuoteSectionData(data);

      // Update store with new data
      set((state) => ({
        homePageData: state.homePageData
          ? {
            ...state.homePageData,
            quoteSection: updatedQuoteSection,
          }
          : null,
        isLoading: false,
      }));

      // No return value needed for Promise<void>
    } catch (error) {
      // Handle error
      console.error('Failed to update Quote section:', error);

      // Set error state
      set({
        error: error instanceof Error ? error.message : 'Failed to update Quote section',
        isLoading: false,
      });

      throw error;
    }
  },

  /**
   * Update Recordings section data
   * @param data Updated Recordings section data
   */
  updateRecordingsSection: async (data: Partial<RecordingsSectionData>) => {
    try {
      // Set loading state
      set({ isLoading: true, error: null });

      // Call API to update Recordings section data
      const updatedRecordingsSection = await HomeService.updateRecordingsSectionData(data);

      // Update store with new data
      set((state) => ({
        homePageData: state.homePageData
          ? {
            ...state.homePageData,
            recordingsSection: updatedRecordingsSection,
          }
          : null,
        isLoading: false,
      }));

      // No return value needed for Promise<void>
    } catch (error) {
      // Handle error
      console.error('Failed to update Recordings section:', error);

      // Set error state
      set({
        error: error instanceof Error ? error.message : 'Failed to update Recordings section',
        isLoading: false,
      });

      throw error;
    }
  },

  /**
   * Update Leaderboard section data.
   * Leaves the page-wide loading flag alone, so saving does not blank the home.
   */
  updateLeaderboardSection: async (data: Partial<LeaderboardSectionData>) => {
    const updated = await HomeService.updateLeaderboardSectionData(data);
    set((state) => ({
      homePageData: state.homePageData
        ? { ...state.homePageData, leaderboardSection: updated }
        : null,
    }));
  }
}));
